import sortBy from 'lodash-es/sortBy';

export function getCompetitionName(competition: {
  name: string;
  parent?: { name: string } | null;
}) {
  if (competition.parent) {
    return `${competition.parent.name} ${competition.name}`;
  }

  return competition.name;
}

export function getCompetitionLogoId(competition: {
  logo?: { id: string } | null;
  parent?: { logo?: { id: string } | null } | null;
}) {
  if (competition.logo) {
    return competition.logo.id;
  }

  if (competition.parent?.logo) {
    return competition.parent.logo.id;
  }

  return;
}

export function getCompetitionUrl(competition: {
  slug: string;
}): `/competition/${string}` {
  return `/competition/${competition.slug}`;
}

export function getSeasonUrl(
  competition: { slug: string },
  season: { slug: string },
): `/competition/${string}/${string}` {
  const competitionSlug = competition.slug;
  const seasonSlug = season.slug.replace(competitionSlug + '-', '');

  return `/competition/${competitionSlug}/${seasonSlug}`;
}

export function getSeasonStandingsUrl(
  competition: { slug: string },
  season: { slug: string },
): `/competition/${string}/${string}/standings` {
  return `${getSeasonUrl(competition, season)}/standings`;
}

export function getMatchdayUrl(
  competition: { slug: string },
  season: { slug: string },
  matchday: { slug: string },
): `/competition/${string}/${string}/${string}` {
  const competitionSlug = competition.slug;
  const seasonSlug = season.slug.replace(competitionSlug + '-', '');
  const matchdaySlug = matchday.slug.replace(season.slug + '-', '');

  return `/competition/${competitionSlug}/${seasonSlug}/${matchdaySlug}`;
}

export function getBattleUrl(battle: { id: string }): `/battle/${string}` {
  return `/battle/${battle.id}`;
}

export function getProfileUrl(profile: {
  username: string;
}): `/profile/${string}` {
  return `/profile/${profile.username}`;
}

export function sortEditions<
  T extends { name: string } | { competition: { name: string } | null },
>(collection: T[]) {
  const priorityMap: Record<string, number> = {
    'World Series': 1,
    'Nueva historia': 2,
    'Internacional': 3,
    'Nacional': 4,
    'others': 5,
    'Under': 6,
    '5 vidas': 7,
  };

  return sortBy(collection, (item) => {
    const name = 'competition' in item ? item.competition!.name : item.name;
    return priorityMap[name] ?? priorityMap['others'];
  });
}

export function sortCompetitions<T extends { name: string }>(collection: T[]) {
  const priorityMap: Record<string, number> = {
    'FMS': 1,
    'Red Bull Batalla': 2,
    'others': 3,
  };

  return sortBy(collection, (item) => {
    return priorityMap[item.name] ?? priorityMap['others'];
  });
}

export function filterNulls<T>(array: (T | null)[]): T[] {
  return array.filter(
    (item): item is NonNullable<typeof item> => item !== null,
  );
}

export function getRoleName(role: string) {
  switch (role) {
    case 'as_freestyler':
      return 'Freestyler';
    case 'as_judge':
      return 'Juez';
    case 'as_host':
      return 'Host';
    case 'as_dj':
      return 'DJ';
    default:
      return role;
  }
}

export function formatEditionName(name: string) {
  const flags: Record<string, string> = {
    'Argentina': '🇦🇷',
    'Bolivia': '🇧🇴',
    'Brasil': '🇧🇷',
    'Chile': '🇨🇱',
    'Colombia': '🇨🇴',
    'Costa Rica': '🇨🇷',
    'Cuba': '🇨🇺',
    'Ecuador': '🇪🇨',
    'El Salvador': '🇸🇻',
    'España': '🇪🇸',
    'Estados Unidos': '🇺🇸',
    'Guatemala': '🇬🇹',
    'Honduras': '🇭🇳',
    'Japón': '🇯🇵',
    'México': '🇲🇽',
    'Nicaragua': '🇳🇮',
    'Panamá': '🇵🇦',
    'Paraguay': '🇵🇾',
    'Perú': '🇵🇪',
    'Puerto Rico': '🇵🇷',
    'República Dominicana': '🇩🇴',
    'Uruguay': '🇺🇾',
    'Venezuela': '🇻🇪',
  };

  return flags[name] ? `${flags[name]} ${name}` : name;
}
